import { forwardRef, type FocusEvent, type InputHTMLAttributes } from 'react';
import { formatDecimal, normalizeTyping, parseScaled } from './numbers';

/** decimal: a number with a comma; integer: whole number; money: złoty, padded to two decimals when you leave the field. */
export type NumberKind = 'decimal' | 'integer' | 'money';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> & {
  value: string;
  onValueChange: (value: string) => void;
  kind?: NumberKind | undefined;
};

/**
 * The one numeric field of the app (rule 11 of docs/wyglad.md): a text field (no browser spinner) that opens the
 * numeric keypad on phones, takes "." and "," alike (also from the desktop numeric keypad), shows a comma.
 */
export const NumberInput = forwardRef<HTMLInputElement, Props>(function NumberInput({ value, onValueChange, kind = 'decimal', onBlur, ...rest }, ref) {
  function blur(e: FocusEvent<HTMLInputElement>) {
    let next = value.trim();
    if (kind === 'money') {
      const grosze = parseScaled(next.replace(/\s*zł$/i, ''), 2);
      if (grosze !== null) next = formatDecimal(grosze / 100, { digits: 2 });
    }
    if (next !== value) onValueChange(next);
    onBlur?.(e);
  }
  return (
    <input
      ref={ref}
      type="text"
      inputMode={kind === 'integer' ? 'numeric' : 'decimal'}
      autoComplete="off"
      {...rest}
      value={value}
      onChange={(e) => onValueChange(kind === 'integer' ? e.target.value : normalizeTyping(e.target.value))}
      onBlur={blur}
    />
  );
});
